/**
 * Champ « adresse » avec suggestions de la Base Adresse Nationale.
 *
 * api-adresse.data.gouv.fr est la source officielle (gratuite, sans clé) déjà
 * utilisée côté serveur pour le géocodage (crm/enrichment.py) : une adresse
 * choisie ici est donc géocodable telle quelle, et le code postal, la ville et
 * le département arrivent avec — autant de champs en moins à saisir, et plus
 * de faute de frappe sur le code postal qui pilote la sectorisation.
 *
 * L'appel part du navigateur (l'API autorise CORS) plutôt que de rebondir par
 * le backend, un champ d'autocomplétion interrogeant à chaque frappe. `fetch`
 * et non le client axios du CRM, qui joindrait le jeton d'authentification à
 * une requête tierce.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";

const BAN_URL = "https://api-adresse.data.gouv.fr/search/";
const MIN_CHARS = 3;
const DEBOUNCE_MS = 250;

/** Champs CRM déduits d'une suggestion (le département vient du contexte
 *  « 38, Isère, Auvergne-Rhône-Alpes »). */
function toCrmFields(feature) {
  const p = feature.properties || {};
  const [longitude, latitude] = feature.geometry?.coordinates || [];
  return {
    adresse: p.name || p.label || "",
    code_postal: p.postcode || "",
    ville: p.city || "",
    departement: (p.context || "").split(",")[0].trim(),
    latitude, longitude,
    label: p.label || "",
  };
}

export default function AddressAutocomplete({
  value, onChange, onSelect, placeholder = "12 rue de la République…",
  className = "", inputClassName = "", testId = "address-autocomplete",
  disabled = false, limit = 6,
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const box = useRef(null);
  const abort = useRef(null);
  // Adresse retenue dans la liste : sans ce garde-fou, le champ rouvrirait
  // aussitôt une suggestion sur le texte qu'on vient d'y écrire.
  const chosen = useRef(null);

  useEffect(() => {
    const q = (value || "").trim();
    if (disabled || q.length < MIN_CHARS || q === chosen.current) { setItems([]); return; }
    const timer = setTimeout(async () => {
      abort.current?.abort();
      const ctrl = new AbortController();
      abort.current = ctrl;
      setLoading(true);
      try {
        const url = `${BAN_URL}?q=${encodeURIComponent(q)}&limit=${limit}&autocomplete=1`;
        const res = await fetch(url, { signal: ctrl.signal });
        const data = res.ok ? await res.json() : null;
        const feats = data?.features || [];
        setItems(feats);
        setActive(-1);
        setOpen(feats.length > 0);
      } catch {
        // Réseau ou API indisponible : la saisie libre reste possible.
        if (!ctrl.signal.aborted) setItems([]);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value, disabled, limit]);

  useEffect(() => {
    const outside = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);

  const choose = useCallback((feature) => {
    const fields = toCrmFields(feature);
    chosen.current = fields.adresse;
    onChange?.(fields.adresse);
    onSelect?.(fields);
    setOpen(false);
    setItems([]);
    setActive(-1);
  }, [onChange, onSelect]);

  const onKeyDown = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i <= 0 ? items.length : i) - 1); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(items[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={box} className={`relative ${className}`}>
      <input
        data-testid={testId}
        value={value || ""}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        onChange={(e) => { chosen.current = null; onChange?.(e.target.value); }}
        onFocus={() => { if (items.length) setOpen(true); }}
        onKeyDown={onKeyDown}
        className={inputClassName}
      />
      {loading && (
        <Loader2 size={14} className="animate-spin text-slate-300 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      )}
      {open && items.length > 0 && (
        <ul role="listbox" data-testid={`${testId}-list`}
          className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1">
          {items.map((f, i) => {
            const p = f.properties || {};
            return (
              <li key={p.id || i} role="option" aria-selected={i === active}
                data-testid={`${testId}-option`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); choose(f); }}
                className={`flex items-start gap-2 px-3 py-2 cursor-pointer ${i === active ? "bg-indigo-50" : ""}`}>
                <MapPin size={13} className="mt-0.5 shrink-0 text-slate-400" />
                <span className="min-w-0">
                  <span className="block text-[13px] text-slate-800 truncate">{p.name}</span>
                  <span className="block text-[11.5px] text-slate-400 truncate">
                    {[p.postcode, p.city, p.context].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
